import { useCallback, useMemo, useRef } from "react";
import { DatePicker as AntDatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import styled from "styled-components";

// #region SCOPED CSS
const StyledWrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    width: ${({ $width }) => $width || "auto"};

    /* The visible custom trigger (child content or default trigger) */
    .dp-trigger {
        position: relative;
        cursor: pointer;
        width: 100%;
    }
    .dp-trigger.disabled,
    .dp-trigger.disabled * {
        cursor: not-allowed !important;
    }

    /* Hide the native antd DatePicker input — it sits on top as an invisible click target */
    .dp-hidden-picker {
        position: absolute;
        inset: 0;
        z-index: 2;
        opacity: 0;
    }
    .dp-hidden-picker .ant-picker {
        width: 100%;
        height: 100%;
        cursor: pointer;
    }

    /* Allow rightContent inside FormField (e.g., X clear button) to sit above the hidden picker */
    .dp-trigger .Frame48RightContent {
        position: relative;
        z-index: 3;
    }

    /* When disabled, block pointer events on the hidden picker */
    &.dp-disabled .dp-hidden-picker {
        pointer-events: none;
    }
`;
// #endregion

// #region CONSTANTS
const DEFAULT_FORMAT = "DD/MM/YYYY";
const DEFAULT_PLACEHOLDER = "Select date";

const toAntdDate = date => {
    if (!date) return null;

    const parsedDate = moment.isMoment(date) ? date.clone() : moment(date.valueOf?.() ?? date);
    if (!parsedDate.isValid()) return null;

    return dayjs(parsedDate.valueOf());
};

/**
 * Predefined lock presets for quick usage.
 * Usage: <DatePicker disableAfter="today" />
 *        <DatePicker disableBefore="today" />
 */
const LOCK_RESOLVERS = {
    today: () => moment().endOf("day"),
    yesterday: () => moment().subtract(1, "day").endOf("day"),
    tomorrow: () => moment().add(1, "day").startOf("day"),
    startOfWeek: () => moment().startOf("week"),
    endOfWeek: () => moment().endOf("week"),
    startOfMonth: () => moment().startOf("month"),
    endOfMonth: () => moment().endOf("month"),
    startOfYear: () => moment().startOf("year"),
    endOfYear: () => moment().endOf("year")
};

/**
 * Resolve a lock value to a moment instance.
 * Accepts: "today" | "yesterday" | "tomorrow" | "startOfMonth" | ... | moment() | Date | "YYYY-MM-DD"
 */
const resolveLockDate = lockValue => {
    if (!lockValue) return null;
    if (typeof lockValue === "string" && LOCK_RESOLVERS[lockValue]) return LOCK_RESOLVERS[lockValue]();
    return moment(lockValue);
};
// #endregion

// #region COMPONENT DOCS
/**
 * DatePicker — A reusable, project-standard wrapper around Ant Design's DatePicker.
 * Single date selection variant. Mirrors the DateRangePicker API for consistency.
 *
 * @param {Object}          props
 *
 * --- Core ---
 * @param {moment}           [props.value]           - Controlled value as a moment object.
 * @param {Function}         [props.onChange]         - Called with (date, dateString) when the date changes.
 * @param {string}           [props.format]          - Display/output format. Default: "YYYY-MM-DD".
 * @param {string}           [props.placeholder]     - Text shown when no date is selected.
 *
 * --- Date Locking ---
 * @param {string|moment|Date} [props.disableBefore] - Disable all dates before this date.
 *                                                     Accepts named presets: "today", "yesterday", "tomorrow",
 *                                                     "startOfWeek", "endOfWeek", "startOfMonth", "endOfMonth",
 *                                                     "startOfYear", "endOfYear".
 *                                                     Or a moment instance, Date, or date string.
 * @param {string|moment|Date} [props.disableAfter]  - Disable all dates after this date. Same formats as above.
 * @param {Function}           [props.disabledDate]  - Custom disabledDate function (receives current moment).
 *                                                     If provided together with disableBefore/disableAfter,
 *                                                     all constraints are combined (all must pass).
 *
 * --- Picker Mode ---
 * @param {string}           [props.picker]          - Antd picker mode: "date" | "week" | "month" | "quarter" | "year".
 *                                                     Default: "date".
 *
 * --- Appearance ---
 * @param {boolean}          [props.disabled]        - Disables the picker.
 * @param {string}           [props.className]       - Additional CSS class on the wrapper.
 * @param {Object}           [props.style]           - Inline styles on the wrapper.
 * @param {string}           [props.width]           - CSS width for the wrapper (e.g. "235px", "100%").
 * @param {string}           [props.popupClassName]  - Additional CSS class on the calendar popup.
 * @param {Object}           [props.popupStyle]      - Inline styles on the calendar popup.
 * @param {boolean}          [props.allowClear]      - Show clear button. Default: true.
 * @param {React.ReactNode}  [props.children]        - Custom trigger content. If not provided, a default
 *                                                     trigger with calendar icon + placeholder text is rendered.
 *
 * --- Passthrough ---
 * @param {Object}           [props.pickerProps]     - Any additional props to pass directly to antd DatePicker.
 */
// #endregion

export default function DatePicker({
    // Core
    value,
    onChange,
    format = DEFAULT_FORMAT,
    placeholder = DEFAULT_PLACEHOLDER,

    // Date locking
    disableBefore,
    disableAfter,
    disabledDate: customDisabledDate,

    // Initial calendar month when empty
    defaultPickerValue,

    // Picker mode
    picker = "date",

    // Appearance
    disabled = false,
    className = "",
    style,
    width,
    popupClassName = "",
    popupStyle,
    allowClear = true,
    children,

    // Passthrough
    pickerProps = {}
}) {
    // #region REFS
    const wrapperRef = useRef(null);
    // #endregion

    // #region METHODS
    /**
     * Combines disableBefore, disableAfter, and custom disabledDate into a single function.
     */
    const mergedDisabledDate = useCallback(
        current => {
            if (!current) return false;

            // Antd v5 passes dayjs objects — convert to moment for comparison
            const currentMoment = moment(current.valueOf());
            const beforeDate = resolveLockDate(disableBefore);
            const afterDate = resolveLockDate(disableAfter);

            if (beforeDate && currentMoment.isBefore(beforeDate, "day")) return true;
            if (afterDate && currentMoment.isAfter(afterDate, "day")) return true;
            if (customDisabledDate && customDisabledDate(currentMoment)) return true;

            return false;
        },
        [disableBefore, disableAfter, customDisabledDate]
    );

    const handleChange = useCallback(
        (date, dateString) => {
            if (!onChange) return;

            // Antd v5 returns dayjs objects internally — convert to moment for project consistency
            const momentDate = date ? moment(date.valueOf()) : null;
            onChange(momentDate, dateString);
        },
        [onChange]
    );
    // #endregion

    // #region COMPUTED PROPS
    const formattedDisplay = useMemo(() => {
        if (!value) return null;
        return moment(value).format(format);
    }, [value, format]);

    const pickerValue = useMemo(() => toAntdDate(value), [value]);

    const antdDefaultPickerValue = useMemo(() => {
        const target = defaultPickerValue || disableBefore;
        if (!target) return undefined;
        return toAntdDate(resolveLockDate(target)) || undefined;
    }, [defaultPickerValue, disableBefore]);

    const finalPopupClassName = `range-picker-pop-up ${popupClassName} ${disabled ? "d-none" : ""}`.trim();
    // #endregion

    // #region RENDER
    return (
        <StyledWrapper
            ref={wrapperRef}
            className={`date-picker ${disabled ? "dp-disabled" : ""} ${className}`}
            style={style}
            $width={width}>
            {/* Invisible antd DatePicker — captures clicks and shows the calendar popup */}
            <div className="dp-hidden-picker">
                <AntDatePicker
                    value={pickerValue}
                    onChange={handleChange}
                    format={format}
                    picker={picker}
                    disabledDate={mergedDisabledDate}
                    defaultPickerValue={antdDefaultPickerValue}
                    allowClear={allowClear}
                    popupClassName={finalPopupClassName}
                    popupStyle={popupStyle}
                    getPopupContainer={() => wrapperRef.current || document.body}
                    style={{
                        opacity: 0,
                        width: "100%",
                        height: "100%"
                    }}
                    {...pickerProps}
                />
            </div>

            {/* Visible trigger — either custom children or the default trigger */}
            <div className={`dp-trigger ${disabled ? "disabled" : ""}`}>
                {children || (
                    <DefaultTrigger
                        displayText={formattedDisplay}
                        placeholder={placeholder}
                        disabled={disabled}
                        hasValue={!!formattedDisplay}
                    />
                )}
            </div>
        </StyledWrapper>
    );
    // #endregion
}

// #region SMALL COMPONENTS
const DefaultTriggerStyled = styled.div`
    background: var(--backgrounds-and-lines-n-400, #1e2631);
    border-radius: 4px;
    padding: 8px;
    display: flex;
    flex-direction: row;
    gap: 5px;
    align-items: center;
    justify-content: flex-start;
    height: 37px;
    width: 100%;
    border: 1px solid transparent;
    transition: border-color 0.2s ease;

    &.has-value {
        border-color: #0060cc;
    }
    &.disabled {
        cursor: not-allowed !important;
    }
    &.disabled i,
    &.disabled .dp-label {
        color: var(--text-icons-gray-300, #4f5b6d);
    }

    i {
        flex-shrink: 0;
        font-size: 12px;
    }

    .dp-label {
        color: var(--text-and-icons-gray-200, #7c868b);
        font-size: 12px;
        font-weight: 400;
        font-style: italic;
        flex: 1;
        line-height: normal;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    &.has-value .dp-label {
        color: #ffffff;
        font-style: normal;
    }

    .dp-chevron {
        font-size: 10px;
        flex-shrink: 0;
    }
`;

const DefaultTrigger = ({ displayText, placeholder, disabled, hasValue }) => (
    <DefaultTriggerStyled className={`${disabled ? "disabled" : ""} ${hasValue ? "has-value" : ""}`}>
        <i className="fa-light fa-calendar" />
        <span className="dp-label">{displayText || placeholder}</span>
        <i className="fa-light fa-chevron-down dp-chevron" />
    </DefaultTriggerStyled>
);
// #endregion
